import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, getIn } from 'formik';
import * as Yup from 'yup';
import { useDebouncedCallback } from 'use-debounce';
import {
  Button,
  CloseButton,
  Combobox,
  Group,
  Input,
  Loader,
  Paper,
  PasswordInput,
  Pill,
  ScrollArea,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  TextInput,
  useCombobox,
} from '@mantine/core';
import { useId } from '@mantine/hooks';
import { Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext.tsx';
import {
  employeeAPI,
  territoriesAPI,
  warehousesAPI,
  vehicleAPI,
  regionAPI,
} from '../services/api.ts';
import toast from '../lib/toast.ts';
interface SearchableOption {
  work_email?: any;
  vehicle_id?: number;
  id: number;
  name: string;
  display_name?: string;
}

// One titled card of the form.
const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) => (
  <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
    <Stack gap="md">
      <div>
        <Text size="lg" fw={600}>
          {title}
        </Text>
        {description && (
          <Text size="sm" c="dimmed">
            {description}
          </Text>
        )}
      </div>
      {children}
    </Stack>
  </Paper>
);

interface LookupInputProps<T> {
  label?: string;
  'aria-label'?: string;
  placeholder?: string;
  withAsterisk?: boolean;
  disabled?: boolean;
  /** Text in the box. Left out, the box keeps its own text. */
  value?: string;
  onChange: (value: string) => void;
  loading?: boolean;
  /** Lookup results to offer; the list is hidden while this is empty. */
  items: T[];
  getKey: (item: T) => React.Key;
  renderItem: (item: T) => React.ReactNode;
  isSelected?: (item: T) => boolean;
  onPick: (item: T) => void;
  showClear?: boolean;
  onClear?: () => void;
  error?: React.ReactNode;
  /** Rendered under the box, above the error (selected pills, hidden fields). */
  children?: React.ReactNode;
}

/**
 * A text box that offers the results of a search-as-you-type lookup. The
 * caller owns the text, the lookup and what picking a result does.
 */
function LookupInput<T>({
  label,
  'aria-label': ariaLabel,
  placeholder,
  withAsterisk,
  disabled,
  value,
  onChange,
  loading,
  items,
  getKey,
  renderItem,
  isSelected,
  onPick,
  showClear,
  onClear,
  error,
  children,
}: LookupInputProps<T>) {
  const id = useId();
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  return (
    <Input.Wrapper
      id={id}
      label={label}
      withAsterisk={withAsterisk}
      error={error}
    >
      <Combobox
        store={combobox}
        onOptionSubmit={(index) => {
          onPick(items[Number(index)]);
          combobox.closeDropdown();
        }}
      >
        <Combobox.Target>
          <TextInput
            id={id}
            aria-label={ariaLabel}
            placeholder={placeholder}
            autoComplete="off"
            disabled={disabled}
            error={!!error}
            {...(value !== undefined ? { value } : {})}
            onChange={(event) => {
              onChange(event.currentTarget.value);
              combobox.openDropdown();
            }}
            onFocus={() => combobox.openDropdown()}
            onClick={() => combobox.openDropdown()}
            onBlur={() => combobox.closeDropdown()}
            rightSection={
              loading ? (
                <Loader size={16} />
              ) : showClear && onClear ? (
                <CloseButton
                  size="sm"
                  aria-label="Clear"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={onClear}
                />
              ) : null
            }
          />
        </Combobox.Target>
        <Combobox.Dropdown hidden={items.length === 0}>
          <Combobox.Options>
            <ScrollArea.Autosize mah={240} type="scroll">
              {items.map((item, index) => {
                const selected = isSelected ? isSelected(item) : false;
                return (
                  <Combobox.Option
                    key={getKey(item)}
                    value={String(index)}
                    active={selected}
                  >
                    {isSelected ? (
                      <Group gap="xs" justify="space-between" wrap="nowrap">
                        <div>{renderItem(item)}</div>
                        {selected && (
                          <Check
                            size={14}
                            color="var(--mantine-color-green-filled)"
                          />
                        )}
                      </Group>
                    ) : (
                      renderItem(item)
                    )}
                  </Combobox.Option>
                );
              })}
            </ScrollArea.Autosize>
          </Combobox.Options>
        </Combobox.Dropdown>
      </Combobox>
      {children}
    </Input.Wrapper>
  );
}

// Define validation schema
const EmployeeSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  work_email: Yup.string().email('Invalid email').required('Email is required'),
  mobile_phone: Yup.string().required('Mobile phone is required'),
  territory_ids: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one territory is required')
    .required('Territory is required'),
  warehouse_ids: Yup.array()
    .of(Yup.number())
    .min(1, 'At least one warehouse is required')
    .required('Warehouse is required'),
  is_territory_manager: Yup.boolean(),
  is_region_manager: Yup.boolean(),
  report_to: Yup.number().when(['is_region_manager'], {
    is: (isRegionManager: boolean) => {
      return isRegionManager; // Only hide manager field for Region Managers
    },
    then: () => Yup.number().notRequired(),
    otherwise: () => Yup.number().required('Manager is required'),
  }),
  vehicle_id: Yup.number().required('Vehicle is required'),
  region_id: Yup.number().required('Region is required'),
  meter_reading: Yup.number().nullable(),
  password: Yup.string().when('id', {
    is: (val: number) => !val,
    then: () =>
      Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required for new employees'),
    otherwise: () => Yup.string().notRequired(),
  }),
});
export const UserForm = ({ initialValues, onSubmit, onCancel }: any) => {
  const { project } = useAuth();
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [territories, setTerritories] = useState<SearchableOption[]>([]);
  const [warehouses, setWarehouses] = useState<SearchableOption[]>([]);
  const [vehicles, setVehicles] = useState<any[]>([]);
  const [managers, setManagers] = useState<SearchableOption[]>([]);
  const [regions, setRegions] = useState<SearchableOption[]>([]);
  const [selectedManagerType, setSelectedManagerType] = useState({
    territoryManager: false,
    regionManager: false,
  });
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState({
    employee: false,
    territory: false,
    warehouse: false,
    vehicle: false,
    manager: false,
    region: false,
  });
  const [selectedTerritories, setSelectedTerritories] = useState<
    SearchableOption[]
  >([]);
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedRegionId, setSelectedRegionId] = useState('');
  const [territorySearchTerm, setTerritorySearchTerm] = useState('');
  const [selectedWarehouses, setSelectedWarehouses] = useState<
    SearchableOption[]
  >([]);
  const [warehouseSearchTerm, setWarehouseSearchTerm] = useState('');
  const [selectedManager, setSelectedManager] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState<any>('');
  const [showPassword, setShowPassword] = useState(false);
  const formikRef = useRef<any>(null);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        if (initialValues.territory_ids?.length) {
          const territoryPromises = initialValues.territory_ids.map(
            (id: number) => territoriesAPI.getById(id)
          );
          const territoryResponses = await Promise.all(territoryPromises);
          const validTerritories = territoryResponses
            .filter((res) => res.success)
            .map((res) => res.data);
          setSelectedTerritories(validTerritories);
        }
        if (initialValues.warehouse_ids?.length) {
          const warehousePromises = initialValues.warehouse_ids.map(
            (id: number) => warehousesAPI.getById(id)
          );
          const warehouseResponses = await Promise.all(warehousePromises);
          const validWarehouses = warehouseResponses
            .filter((res) => res.success)
            .map((res) => res.data);
          setSelectedWarehouses(validWarehouses);
        }
        if (initialValues.vehicle_id) {
          const vehicleRes = await vehicleAPI.getById(initialValues.vehicle_id);
          if (vehicleRes.success && vehicleRes.data) {
            setVehicles([vehicleRes.data]);
            setSelectedVehicle(vehicleRes.data.name);
          }
        }
        if (initialValues.report_to) {
          const managerRes = await employeeAPI.getById(initialValues.report_to);
          if (managerRes.success) {
            setManagers([managerRes.data]);
            setSelectedManager(managerRes.data.name);
          }
        }

        if (initialValues.region_id) {
          const regionRes = await regionAPI.getById(initialValues.region_id);
          if (regionRes.success) {
            setRegions([regionRes.data.region]);
            setSelectedRegion(regionRes.data.region.name);
            setSelectedRegionId(regionRes.data.region.id.toString());
          }
        }

        if (initialValues?.is_territory_manager) {
          setSelectedManagerType((prev) => ({
            ...prev,
            territoryManager: true,
          }));
        }
        if (initialValues?.is_region_manager) {
          setSelectedManagerType((prev) => ({
            ...prev,
            regionManager: true,
          }));
        }
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [initialValues]);

  const handleSearch = useDebouncedCallback(
    async (type: string, searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) {
        // Clear results when search term is empty or too short
        switch (type) {
          case 'territory':
            setTerritories([]);
            break;
          case 'warehouse':
            setWarehouses([]);
            break;
          case 'region':
            setRegions([]);
            break;
          case 'vehicle':
            setVehicles([]);
            break;
          case 'manager':
            setManagers([]);
            break;
        }
        return;
      }

      // Get company_id from project
      const company_id = project?.company_id?.id || project?.company_id;

      if (!company_id) {
        console.error('Company ID not found');
        return;
      }

      setSearchLoading((prev) => ({ ...prev, [type]: true }));
      try {
        let response;
        switch (type) {
          case 'territory':
            response = await territoriesAPI.search(searchTerm, company_id);
            if (response.data.success) {
              // filter territorues with selected region
              const filteredTerritories = response.data.data.filter(
                (territory: any) =>
                  territory.region_id === Number(selectedRegionId)
              );

              setTerritories(filteredTerritories);
            }
            break;
          case 'warehouse':
            response = await warehousesAPI.search(searchTerm, company_id);
            if (response.data.success) {
              setWarehouses(response.data.data);
            }
            break;
          case 'region':
            response = await regionAPI.search(searchTerm);
            if (response.success) {
              setRegions(response.data.regions);
            }
            break;
          case 'vehicle':
            response = await vehicleAPI.search(searchTerm, company_id);
            if (response.data.success) {
              setVehicles(response.data.data);
            }
            break;
          case 'manager':
            response = await employeeAPI.search(searchTerm, company_id);
            if (response.success) {
              // Filter out the current employee from manager search results if editing
              const filteredManagers = response.data.filter(
                (emp: any) => !initialValues.id || emp.id !== initialValues.id
              );
              setManagers(filteredManagers);
            }
            break;
        }
      } catch (error) {
        console.error(`Error searching ${type}:`, error);
      } finally {
        setSearchLoading((prev) => ({ ...prev, [type]: false }));
      }
    },
    800
  );

  // Debounced search handlers for individual fields - only handle search, not state updates
  const debouncedRegionSearch = useDebouncedCallback((value: string) => {
    handleSearch('region', value);
  }, 300);

  const debouncedTerritorySearch = useDebouncedCallback((value: string) => {
    handleSearch('territory', value);
  }, 300);

  const debouncedWarehouseSearch = useDebouncedCallback((value: string) => {
    handleSearch('warehouse', value);
  }, 300);

  const debouncedVehicleSearch = useDebouncedCallback((value: string) => {
    handleSearch('vehicle', value);
  }, 300);
  // Update employee search handler
  const handleEmployeeSearch = useDebouncedCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2) {
        setSearchResults([]);
        return;
      }
      setSearchLoading((prev) => ({ ...prev, employee: true }));
      try {
        const company_id = project?.company_id;
        const response = await employeeAPI.search(searchTerm, company_id);
        if (response.success) {
          setSearchResults(response.data);
        }
      } catch (error) {
        console.error('Error searching employees:', error);
      } finally {
        setSearchLoading((prev) => ({ ...prev, employee: false }));
      }
    },
    800
  );
  // Update function to handle employee selection
  const handleEmployeeSelect = async (employee: any, setFieldValue: any) => {
    // Set basic information
    setFieldValue('name', employee.name);
    setFieldValue('work_email', employee.work_email);
    setFieldValue('mobile_phone', employee.mobile_phone);
    setFieldValue('id', employee.id);

    try {
      // Fetch and set territory data
      if (employee.territory_ids?.length) {
        const territoryPromises = employee.territory_ids.map((id: number) =>
          territoriesAPI.getById(id)
        );
        const territoryResponses = await Promise.all(territoryPromises);
        const validTerritories = territoryResponses
          .filter((res: any) => res.success)
          .map((res: any) => res.data);

        setTerritories(validTerritories);
        setFieldValue('territory_ids', employee.territory_ids);
        setSelectedTerritories(validTerritories);
      }

      // Fetch and set warehouse data
      if (employee.warehouse_ids?.length) {
        const warehousePromises = employee.warehouse_ids.map((id: number) =>
          warehousesAPI.getById(id)
        );
        const warehouseResponses = await Promise.all(warehousePromises);
        const validWarehouses = warehouseResponses
          .filter((res: any) => res.success)
          .map((res: any) => res.data);

        setWarehouses(validWarehouses);
        setFieldValue('warehouse_ids', employee.warehouse_ids);
        setSelectedWarehouses(validWarehouses);
      }

      // Fetch and set vehicle data
      if (employee.vehicle_id) {
        const vehicleRes = await vehicleAPI.getById(employee.vehicle_id);
        if (vehicleRes.success) {
          setVehicles([vehicleRes.data]);
          setFieldValue('vehicle_id', employee.vehicle_id);
          setSelectedVehicle(vehicleRes?.data?.name);
        }
      }

      // Fetch and set manager data
      if (employee.report_to) {
        const managerRes = await employeeAPI.getById(employee.report_to);
        if (managerRes.success) {
          // setManagers([managerRes.data]);
          setFieldValue('report_to', employee.report_to);
          setSelectedManager(managerRes.data.name);
        }
      }

      // Clear search results after selection
      setSearchResults([]);
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  const isEditMode = Boolean(initialValues?.id);

  // if region manager is selected, fetch All territories by region
  useEffect(() => {
    if (
      selectedRegionId &&
      selectedManagerType.regionManager &&
      !isEditMode &&
      !selectedTerritories.length // Only fetch if no territories are manually selected
    ) {
      const fetchTerritoriesByRegion = async () => {
        try {
          const response = await territoriesAPI.getByRegionId(
            Number(selectedRegionId)
          );
          if (response.success && response.data && formikRef.current) {
            // Only update if form hasn't been manually populated with territories
            if (!formikRef.current.values.territory_ids?.length) {
              setSelectedTerritories(response.data || []);
              formikRef.current.setFieldValue(
                'territory_ids',
                response.data.map((t: any) => t.id)
              );
            }
          }
        } catch (error) {
          console.error('Error fetching territories by region:', error);
        }
      };
      fetchTerritoriesByRegion();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRegionId,
    selectedManagerType.regionManager,
    isEditMode,
    selectedTerritories.length,
  ]);

  const handleSubmit = async (
    values: any,
    { setSubmitting, setErrors }: any
  ) => {
    try {
      const formattedValues = {
        ...values,
        territory_ids: values.territory_ids.map(Number),
        warehouse_ids: values.warehouse_ids.map(Number),
        report_to: values.is_region_manager
          ? Number(values.id) || null // Region managers report to themselves
          : Number(values.report_to) || null,
        vehicle_id: values.vehicle_id ? Number(values.vehicle_id) : '',
        meter_reading: values.meter_reading ? Number(values.meter_reading) : '',
        company_id: Number(project?.id),
        project_id: Number(project?.id),
        employee_type: 'employee',
        id: values.id ? Number(values.id) : undefined,
        region_id: values.region_id || '',
        is_territory_manager: values.is_territory_manager || false,
        manager_id: values.is_region_manager
          ? Number(values.id)
          : values.report_to,
        is_region_manager: values.is_region_manager || false,
      };

      if (selectedTerritories.length > 0) {
        formattedValues.territory_ids = selectedTerritories.map((t) => t.id);
      }

      await onSubmit(formattedValues);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message ||
          error.message ||
          'An error occurred during submission'
      );
      // Check if the error is a duplicate email error
      if (error.response?.data?.error === 'DUPLICATE_EMAIL') {
        setErrors({
          work_email: 'This email address is already in use',
          submit: 'Email address is already registered',
        });
        return;
      }
      // Handle other errors
      setErrors({
        submit:
          error.response?.data?.message ||
          error.message ||
          'An error occurred during submission',
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Group justify="center" h={160}>
        <Loader />
      </Group>
    );
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        id: initialValues?.id || formikRef.current?.values?.id || '',
        name: initialValues?.name || '',
        work_email: initialValues?.work_email || '',
        mobile_phone: initialValues?.mobile_phone || '',
        territory_ids: initialValues?.territory_ids,
        warehouse_ids: initialValues?.warehouse_ids || [],
        report_to: initialValues?.report_to || '',
        vehicle_id: initialValues?.vehicle_id || '',
        meter_reading: initialValues?.meter_reading || '',
        is_territory_manager: initialValues?.is_territory_manager || false,
        is_region_manager: initialValues?.is_region_manager || false,
        region_id: initialValues?.region_id || '',
        password: initialValues?.password || '',
        company_id: project?.id || '',
        project_id: project?.id || '',
        employee_type: 'employee',
        is_edit_mode: isEditMode,
      }}
      validationSchema={EmployeeSchema}
      onSubmit={handleSubmit}
    >
      {({
        isSubmitting,
        setFieldValue,
        errors,
        touched,
        values,
        getFieldProps,
      }) => {
        // Same rule as Formik's ErrorMessage: shown once the field is touched.
        const fieldError = (name: string) =>
          getIn(touched, name) && getIn(errors, name)
            ? getIn(errors, name)
            : undefined;

        return (
          <Form>
            <Stack gap="lg">
              {/* Employee Search Section - Only show in Create mode */}
              {!isEditMode && (
                <Section
                  title="Start from an existing employee"
                  description="Pick someone already in the system to fill in their details."
                >
                  <LookupInput
                    aria-label="Search Existing Employee"
                    placeholder="Search by name, email, or phone..."
                    onChange={(value) => handleEmployeeSearch(value)}
                    loading={searchLoading.employee}
                    items={searchResults}
                    getKey={(employee) => employee.id}
                    renderItem={(employee) => (
                      <>
                        <Text size="sm" fw={500}>
                          {employee.name}
                        </Text>
                        <Text size="sm" c="dimmed">
                          {employee.work_email}
                        </Text>
                      </>
                    )}
                    onPick={(employee) => {
                      handleEmployeeSelect(employee, setFieldValue);
                      setSearchResults([]); // Clear results after selection
                    }}
                    showClear={searchResults.length > 0}
                    onClear={() => {
                      setSearchResults([]);
                    }}
                  />
                </Section>
              )}

              <Section
                title="Basic information"
                description="Who this user is and how they sign in."
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  <TextInput
                    label="Name"
                    withAsterisk
                    {...getFieldProps('name')}
                    error={fieldError('name')}
                  />
                  <TextInput
                    label="Email"
                    type="email"
                    withAsterisk
                    {...getFieldProps('work_email')}
                    error={fieldError('work_email')}
                  />
                  <TextInput
                    label="Mobile Phone"
                    withAsterisk
                    {...getFieldProps('mobile_phone')}
                    error={fieldError('mobile_phone')}
                  />
                  <PasswordInput
                    label="Password"
                    withAsterisk={!isEditMode}
                    placeholder={
                      isEditMode
                        ? 'Leave blank to keep current password'
                        : 'Enter password'
                    }
                    visible={showPassword}
                    onVisibilityChange={setShowPassword}
                    {...getFieldProps('password')}
                    error={fieldError('password')}
                  />
                </SimpleGrid>
              </Section>

              <Section
                title="Role and reporting"
                description="A region manager has no manager of their own."
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  {/* Is territory or Region Manager */}
                  <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" wrap="nowrap">
                      <div>
                        <Text size="sm" fw={500}>
                          Territory Manager
                        </Text>
                        <Text size="xs" c="dimmed">
                          Assign as territory manager
                        </Text>
                      </div>
                      <Switch
                        aria-label="Territory Manager"
                        checked={values.is_territory_manager}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          setFieldValue('is_territory_manager', checked);

                          if (checked) {
                            // If territory manager is selected, uncheck region manager
                            setFieldValue('is_region_manager', false);
                            setSelectedManagerType({
                              territoryManager: true,
                              regionManager: false,
                            });
                            // Territory managers still need managers, so don't clear the manager field
                          } else {
                            // If unchecked, update manager type
                            setSelectedManagerType((prev) => ({
                              ...prev,
                              territoryManager: false,
                            }));
                          }
                        }}
                      />
                    </Group>
                  </Paper>
                  <Paper withBorder p="md" radius="md">
                    <Group justify="space-between" wrap="nowrap">
                      <div>
                        <Text size="sm" fw={500}>
                          Region Manager
                        </Text>
                        <Text size="xs" c="dimmed">
                          Assign as region manager
                        </Text>
                      </div>
                      <Switch
                        aria-label="Region Manager"
                        checked={values.is_region_manager}
                        onChange={(event) => {
                          const checked = event.currentTarget.checked;
                          setFieldValue('is_region_manager', checked);

                          if (checked) {
                            // If region manager is selected, uncheck territory manager
                            setFieldValue('is_territory_manager', false);
                            setSelectedManagerType({
                              territoryManager: false,
                              regionManager: true,
                            });
                            // Set themselves as their own manager
                            if (values.id) {
                              setFieldValue('report_to', values.id);
                              setSelectedManager(values.name || '');
                            } else {
                              // If no ID yet (new employee), clear manager field
                              setFieldValue('report_to', '');
                              setSelectedManager('');
                            }
                            setManagers([]);
                          } else {
                            // If unchecked, clear manager and update manager type
                            setFieldValue('report_to', '');
                            setSelectedManager('');
                            setManagers([]);
                            setSelectedManagerType((prev) => ({
                              ...prev,
                              regionManager: false,
                            }));
                          }
                        }}
                      />
                    </Group>
                  </Paper>
                  {/* Manager Search */}
                  {!values.is_region_manager && (
                    <LookupInput
                      label="Manager"
                      withAsterisk
                      placeholder="Search manager..."
                      value={selectedManager}
                      onChange={(value) => {
                        setSelectedManager(value);
                        handleSearch('manager', value);
                      }}
                      loading={searchLoading.manager}
                      items={selectedManager ? managers : []}
                      getKey={(manager) => manager.id}
                      renderItem={(manager) => (
                        <Text size="sm">
                          {manager.name}
                          {manager.work_email && (
                            <Text span size="sm" c="dimmed" ml="xs">
                              ({manager.work_email})
                            </Text>
                          )}
                        </Text>
                      )}
                      onPick={(manager) => {
                        setFieldValue('report_to', manager.id);

                        setSelectedManager(manager.name);
                        setManagers([]); // Clear search results after selection
                      }}
                      showClear={!!selectedManager}
                      onClear={() => {
                        setFieldValue('report_to', '');
                        setSelectedManager('');
                        setManagers([]);
                      }}
                      error={fieldError('report_to')}
                    >
                      <Field type="hidden" name="report_to" />
                    </LookupInput>
                  )}
                </SimpleGrid>
              </Section>

              <Section
                title="Area"
                description="The region, territories and warehouses this user works in."
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  {/* Region Search */}
                  <LookupInput
                    label="Region"
                    withAsterisk
                    placeholder="Search Region..."
                    value={selectedRegion}
                    onChange={(value) => {
                      setSelectedRegion(value);
                      debouncedRegionSearch(value);
                    }}
                    loading={searchLoading.region}
                    items={selectedRegion ? regions : []}
                    getKey={(region) => region.id}
                    renderItem={(region) => <Text size="sm">{region.name}</Text>}
                    onPick={(region) => {
                      setFieldValue('region_id', region.id);
                      setSelectedRegionId(region.id.toString());
                      setSelectedRegion(region.name);
                      setRegions([]); // Clear search results after selection
                    }}
                    showClear={!!selectedRegion}
                    onClear={() => {
                      setFieldValue('region_id', '');
                      setSelectedRegion('');
                      setRegions([]);
                    }}
                    error={fieldError('region_id')}
                  >
                    <Field type="hidden" name="region_id" />
                  </LookupInput>

                  {/* Territory Search */}
                  <LookupInput
                    label="Territories"
                    withAsterisk
                    disabled={!selectedRegion}
                    placeholder="Search territories..."
                    value={territorySearchTerm}
                    onChange={(value) => {
                      setTerritorySearchTerm(value);
                      debouncedTerritorySearch(value);
                    }}
                    loading={searchLoading.territory}
                    items={territorySearchTerm ? territories : []}
                    getKey={(territory) => territory.id}
                    isSelected={(territory) =>
                      selectedTerritories.some((t) => t.id === territory.id)
                    }
                    renderItem={(territory) => (
                      <Text size="sm">{territory.name}</Text>
                    )}
                    onPick={(territory) => {
                      const isSelected = selectedTerritories.some(
                        (t) => t.id === territory.id
                      );
                      if (!isSelected) {
                        const newSelectedTerritories = [
                          ...selectedTerritories,
                          territory,
                        ];
                        setSelectedTerritories(newSelectedTerritories);
                        setFieldValue(
                          'territory_ids',
                          newSelectedTerritories.map((t) => t.id)
                        );
                      }
                      setTerritorySearchTerm('');
                      setTerritories([]);
                    }}
                    error={fieldError('territory_ids')}
                  >
                    {/* Selected Territories Display */}
                    {selectedTerritories.length > 0 && (
                      <Pill.Group mt="xs">
                        {selectedTerritories.map((territory) => (
                          <Pill
                            key={territory.id}
                            withRemoveButton
                            onRemove={() => {
                              const newSelectedTerritories =
                                selectedTerritories.filter(
                                  (t) => t.id !== territory.id
                                );
                              setSelectedTerritories(newSelectedTerritories);
                              setFieldValue(
                                'territory_ids',
                                newSelectedTerritories.map((t) => t.id)
                              );
                            }}
                          >
                            {territory.name}
                          </Pill>
                        ))}
                      </Pill.Group>
                    )}
                    <Field type="hidden" name="territory_ids" />
                  </LookupInput>

                  {/* Warehouse Search */}
                  <LookupInput
                    label="Warehouses"
                    withAsterisk
                    placeholder="Search warehouses..."
                    value={warehouseSearchTerm}
                    onChange={(value) => {
                      setWarehouseSearchTerm(value);
                      debouncedWarehouseSearch(value);
                    }}
                    loading={searchLoading.warehouse}
                    items={warehouseSearchTerm ? warehouses : []}
                    getKey={(warehouse) => warehouse.id}
                    isSelected={(warehouse) =>
                      selectedWarehouses.some((w) => w.id === warehouse.id)
                    }
                    renderItem={(warehouse) => (
                      <Text size="sm">
                        {warehouse.display_name || warehouse.name}
                      </Text>
                    )}
                    onPick={(warehouse) => {
                      const isSelected = selectedWarehouses.some(
                        (w) => w.id === warehouse.id
                      );
                      if (!isSelected) {
                        const newSelectedWarehouses = [
                          ...selectedWarehouses,
                          warehouse,
                        ];
                        setSelectedWarehouses(newSelectedWarehouses);
                        setFieldValue(
                          'warehouse_ids',
                          newSelectedWarehouses.map((w) => w.id)
                        );
                      }
                      setWarehouseSearchTerm('');
                      setWarehouses([]);
                    }}
                    error={fieldError('warehouse_ids')}
                  >
                    {/* Selected Warehouses Display */}
                    {selectedWarehouses.length > 0 && (
                      <Pill.Group mt="xs">
                        {selectedWarehouses.map((warehouse) => (
                          <Pill
                            key={warehouse.id}
                            withRemoveButton
                            onRemove={() => {
                              const newSelectedWarehouses =
                                selectedWarehouses.filter(
                                  (w) => w.id !== warehouse.id
                                );
                              setSelectedWarehouses(newSelectedWarehouses);
                              setFieldValue(
                                'warehouse_ids',
                                newSelectedWarehouses.map((w) => w.id)
                              );
                            }}
                          >
                            {warehouse.display_name || warehouse.name}
                          </Pill>
                        ))}
                      </Pill.Group>
                    )}
                    {/* Hidden field for form submission */}
                    <Field type="hidden" name="warehouse_ids" />
                  </LookupInput>
                </SimpleGrid>
              </Section>

              <Section
                title="Vehicle"
                description="The vehicle this user drives and its starting meter reading."
              >
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
                  {/* Vehicle Search */}
                  <LookupInput
                    label="Vehicle"
                    placeholder="Search vehicle..."
                    value={selectedVehicle}
                    onChange={(value) => {
                      setSelectedVehicle(value);
                      debouncedVehicleSearch(value);
                    }}
                    loading={searchLoading.vehicle}
                    items={selectedVehicle ? vehicles : []}
                    getKey={(vehicle) => vehicle.id}
                    renderItem={(vehicle) => (
                      <Text size="sm">{vehicle.name}</Text>
                    )}
                    onPick={(vehicle) => {
                      setFieldValue('vehicle_id', vehicle.vehicle_id);
                      setSelectedVehicle(vehicle.name || '');
                      setVehicles([]);
                    }}
                    showClear={!!selectedVehicle}
                    onClear={() => {
                      setFieldValue('vehicle_id', '');
                      setSelectedVehicle('');
                      setVehicles([]);
                      console.log('Cleared vehicle_id');
                    }}
                    error={fieldError('vehicle_id')}
                  >
                    <Field type="hidden" name="vehicle_id" />
                  </LookupInput>

                  {/* Add Meter Reading field */}
                  <TextInput
                    label="Meter Reading"
                    placeholder="Enter meter reading"
                    {...getFieldProps('meter_reading')}
                    error={fieldError('meter_reading')}
                  />
                </SimpleGrid>
              </Section>

              {/* Add hidden fields for company_id and project_id */}
              <Field type="hidden" name="company_id" />
              <Field type="hidden" name="project_id" />
              <Field type="hidden" name="employee_type" />

              {/* Form Actions: kept in view while the form scrolls. */}
              <Paper
                withBorder
                shadow="md"
                p="md"
                pos="sticky"
                bottom={16}
                style={{ zIndex: 5 }}
              >
                <Group justify="flex-end" gap="sm">
                  <Button variant="default" onClick={onCancel}>
                    Cancel
                  </Button>
                  <Button type="submit" loading={isSubmitting}>
                    {isEditMode ? 'Save changes' : 'Create user'}
                  </Button>
                </Group>
              </Paper>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
